import { useState, useMemo } from "react";
import { Terminal, CopySimple, Check } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { ansiToHtml } from "@/lib/ansi-to-html";

interface TerminalSnapshotCardProps {
  ansi: string;
  plain: string;
  cwd?: string;
  timestamp?: number;
  compact?: boolean;
}

export function TerminalSnapshotCard({
  ansi,
  plain,
  cwd,
  timestamp,
  compact,
}: TerminalSnapshotCardProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [copied, setCopied] = useState(false);

  const lineCount = plain.split("\n").length;
  const shouldCollapse = lineCount > 10;
  const isCollapsed = shouldCollapse && collapsed;

  const html = useMemo(() => ansiToHtml(ansi), [ansi]);
  const displayHtml = useMemo(() => {
    if (!isCollapsed) return html;
    // Show first 10 lines only when collapsed
    const ansiLines = ansi.split("\n").slice(0, 10);
    return ansiToHtml(ansiLines.join("\n"));
  }, [html, ansi, isCollapsed]);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(plain)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  const timeStr = timestamp
    ? new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : null;

  return (
    <div
      className={cn(
        "my-1 w-full max-w-[34rem] rounded bg-foreground/[0.03] overflow-hidden",
        compact && "text-[12px]",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Terminal size={12} weight="Bold" />
          <span className="uppercase tracking-wide">Terminal Snapshot</span>
          {cwd && (
            <span className="opacity-60 truncate max-w-[20ch]">{cwd}</span>
          )}
          {timeStr && <span className="opacity-60">{timeStr}</span>}
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          title="Copy plain text"
        >
          {copied ? <Check size={12} /> : <CopySimple size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {/* ANSI content */}
      <div
        className="px-3 py-2 overflow-x-auto"
        style={{ maxHeight: isCollapsed ? "200px" : "400px" }}
      >
        <pre
          className="font-mono text-[12px] leading-[1.4] text-foreground whitespace-pre"
          dangerouslySetInnerHTML={{ __html: displayHtml }}
        />
      </div>

      {/* Collapse toggle */}
      {shouldCollapse && (
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="w-full px-3 py-1 text-[11px] text-muted-foreground hover:text-foreground border-t border-border/50 transition-colors text-center"
        >
          {collapsed ? `Show all ${lineCount} lines` : "Show less"}
        </button>
      )}
    </div>
  );
}
